import React from 'react'
import { useQueryParams } from '@fs/zion-router'
import { usePageWidth } from '@fs/zion-ui'
import { useOverlayContext } from '../../../shared/providers/overlayContextProvider/OverlayContext'
import IndexImage from '../../views/indexing/components/indexImage/IndexImage'
import SelectPerson from '../../views/indexing/components/selectPerson/SelectPerson'
import SelectPersonBottomSheet from '../../views/indexing/components/selectPerson/SelectPersonBottomSheet'
import FullText from '../../views/fullText/FullText'
import { useR2StateContext } from '../../providers/r2Provider/R2Context'
import SelectImagePrompt from './SelectImagePrompt'
import useInfoSheetOrientationCachedState from '../../../shared/hooks/useInfoSheetOrientationCachedState'
import TranscriptAndNamesPanel from '../../views/indexing/components/transcriptAndNamesPanel/TranscriptAndNamesPanel'
import OptedInBanner from './components/namesExperienceBanner/OptedInBanner'
import NotOptedInBanner from './components/namesExperienceBanner/NotOptedInBanner'
import useNamesExperienceOptIn from '../../hooks/useNamesExperienceOptIn'

/**
 * SideSheetBody
 *
 * Renders the main body content of the side sheet, conditionally displaying different views
 * based on the updated-Names-experience opt-in state and query parameters (grid view, index view, full text view).
 *
 * - Opted in: Shows TranscriptAndNamesPanel with IndexImage
 * - Not opted in (classic): Shows SelectPerson/SelectPersonBottomSheet with conditional IndexImage and FullText
 *
 * @component
 * @param {Object} props
 * @param {boolean} props.imageHasData - Whether the current image has indexing data
 * @param {boolean} [props.loading=false] - Loading state indicator
 * @returns {JSX.Element}
 */
const SideSheetBody = ({ imageHasData, loading = false }) => {
  const { query } = useQueryParams()
  const { view, grid } = query || {}
  const r2State = useR2StateContext()
  const { inPanelStack } = useOverlayContext()
  const pageWidth = usePageWidth()
  const isMobile = pageWidth({ default: true, sm: false })
  const { isBottomSheet } = useInfoSheetOrientationCachedState()
  const { isOptedIn, loading: optInLoading } = useNamesExperienceOptIn()

  // Defer the experience swap until the opt-in is definitive. Rendering either branch while the
  // preference is still resolving flashes the wrong experience, then swaps once it settles.
  if (optInLoading) return null

  return (
    <>
      {isOptedIn ? (
        <>
          {grid && view === 'index' ? (
            <SelectImagePrompt />
          ) : (
            <>
              <TranscriptAndNamesPanel loading={loading} additionalHeaderContent={<OptedInBanner />} />
              {(imageHasData || inPanelStack('addPersonPage1Generic')) && !r2State.resetState && <IndexImage />}
            </>
          )}
        </>
      ) : (
        <>
          {grid && view === 'index' && <SelectImagePrompt />}
          {!grid && view === 'index' && (
            <>
              {!isMobile && (
                <>
                  {isBottomSheet && <SelectPersonBottomSheet />}
                  {!isBottomSheet && <SelectPerson loading={loading} additionalHeaderContent={<NotOptedInBanner />} />}
                </>
              )}
              {isMobile && <SelectPerson loading={loading} additionalHeaderContent={<NotOptedInBanner />} />}
              {imageHasData && !r2State.resetState && <IndexImage />}
            </>
          )}
          {view === 'fullText' && <FullText />}
        </>
      )}
    </>
  )
}

export default SideSheetBody
